import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

const useAutoLogout = (timeoutMinutes = 30) => {
  const { currentUser, logout } = useAuth();
  const timeoutId = useRef(null);
  const warningTimeoutId = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const showWarningRef = useRef(false); // Track showWarning in ref to avoid stale closures
  const [remainingTime, setRemainingTime] = useState(2);
  const countdownInterval = useRef(null);

  const resetTimer = useCallback(() => {
    // If timeout is 0, don't set any timers (disabled)
    if (timeoutMinutes === 0) {
      return;
    }
    // Clear existing timers
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    if (warningTimeoutId.current) {
      clearTimeout(warningTimeoutId.current);
    }
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
    }
    setShowWarning(false);
    showWarningRef.current = false; // Sync ref with state
    setRemainingTime(2);

    // TODO: Track user activity to Central API (optional enhancement)
    // PUT /api/user/last-activity
    // Payload: { timestamp: Date.now(), activity: 'user_interaction' }
    // NOTE: This is optional for analytics. Current implementation tracks
    // activity client-side only for auto-logout without server sync.
    // Only implement if server-side activity tracking is needed.

    // Only set timer if user is logged in
    if (currentUser) {
      const timeoutMs = timeoutMinutes * 60 * 1000;
      const warningMs = (timeoutMinutes - 2) * 60 * 1000; // Warning 2 minutes before logout

      // Set warning timeout (2 minutes before logout)
      if (timeoutMinutes > 2) {
        warningTimeoutId.current = setTimeout(() => {
          setShowWarning(true);
          showWarningRef.current = true; // Sync ref with state
          setRemainingTime(120); // 2 minutes in seconds
          
          // Start countdown
          let seconds = 120;
          countdownInterval.current = setInterval(() => {
            seconds -= 1;
            setRemainingTime(seconds);
            
            if (seconds <= 0) {
              clearInterval(countdownInterval.current);
            }
          }, 1000);
          
          // Show toast notification
          toast.warning('You will be logged out soon due to inactivity', {
            autoClose: false,
            closeButton: true,
          });
        }, warningMs);
      }

      // Set logout timeout
      timeoutId.current = setTimeout(() => {
        setShowWarning(false);
        showWarningRef.current = false; // Sync ref with state
        logout();
        toast.info('You have been logged out due to inactivity', {
          position: 'top-center',
          autoClose: 5000,
        });
      }, timeoutMs);
    }
  }, [currentUser, timeoutMinutes, logout]);
  
  const handleStayLoggedIn = useCallback(() => {
    setShowWarning(false);
    showWarningRef.current = false; // Sync ref with state
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
    }
    resetTimer();
    toast.dismiss();
    toast.success('Session extended', {
      autoClose: 2000,
    });
  }, [resetTimer]);
  
  const handleLogout = useCallback(() => {
    setShowWarning(false);
    showWarningRef.current = false; // Sync ref with state
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
    }
    logout();
  }, [logout]);

  useEffect(() => {
    if (!currentUser) {
      // Clear timers if user logs out
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      if (warningTimeoutId.current) {
        clearTimeout(warningTimeoutId.current);
      }
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
      setShowWarning(false);
      showWarningRef.current = false; // Sync ref with state
      return;
    }

    // List of events to track user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Reset timer on any user activity
    const resetTimerOnActivity = (event) => {
      // Always reset timer for navigation events (clicking buttons/links)
      // or if not showing warning dialog
      const isNavigationEvent = event.type === 'click' && (
        event.target.closest('a, button') ||
        event.target.tagName === 'A' ||
        event.target.tagName === 'BUTTON'
      );

      // Use ref instead of state to avoid stale closures
      if (!showWarningRef.current || isNavigationEvent) {
        if (isNavigationEvent && showWarningRef.current) {
          // If navigating during warning, dismiss warning and extend session
          setShowWarning(false);
          showWarningRef.current = false; // Sync ref with state
          if (countdownInterval.current) {
            clearInterval(countdownInterval.current);
          }
        }
        resetTimer();
      }
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, resetTimerOnActivity, true);
    });

    // Start the timer
    resetTimer();

    // Cleanup function
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimerOnActivity, true);
      });
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      if (warningTimeoutId.current) {
        clearTimeout(warningTimeoutId.current);
      }
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, [currentUser, timeoutMinutes, resetTimer]); // Removed showWarning to prevent infinite loop

  // Format remaining time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return {
    resetTimer,
    showWarning,
    remainingTime,
    formatTime,
    handleStayLoggedIn,
    handleLogout,
  };
};

export default useAutoLogout;